// The Jobs tab (spec/dashboard.md section 12.2; spec/api-conventions.md section 9): `GET …/jobs` newest first with Load
// more; a row expands to the input and the result or the error (pretty JSON); a failed build's issue-shaped
// `error.details` render as the validation report.
import { useRef, useState } from 'react';
import { useParams } from 'react-router';
import { ops } from '../../api/operations';
import type { Job, Page } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { ValidationReport } from '../../history/ValidationReport';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { formatInstant, useTimeZone, zoneLabel } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { queryCache, useQuery } from '../../workspace/cache';
import { asApiError, issuesOf, JOB_TONE, JOBS_PAGE_SIZE, keys, pretty, type JobError } from './releases';
import { EmptyState } from '../../ui/Banner';
import { ChevronRight, Hourglass, RefreshCw } from '../../ui/icons';
import { LoadError, SkeletonRows } from '../../ui/PageStates';

export function JobsTab() {
  const { branch = '' } = useParams();
  const { org, game, offsets } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const [zone] = useTimeZone();
  const key = keys.jobs(org, game.slug);
  const fetchPage = (cursor: string | null) => client.data(ops.listJobs, { path: { org, game: game.slug }, query: { limit: JOBS_PAGE_SIZE, ...(cursor ? { cursor } : {}) } }) as Promise<Page<Job>>;
  const q = useQuery(key, () => fetchPage(null));
  const first = q.data;

  const [more, setMore] = useState<{ base: Page<Job>; items: Job[]; cursor: string | null } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const firstRef = useRef(first);
  firstRef.current = first;
  const extra = more && more.base === first ? more : null;
  const rows = [...(first?.items ?? []), ...(extra?.items ?? [])];
  const nextCursor = extra ? extra.cursor : (first?.nextCursor ?? null);

  const loadMore = async () => {
    if (!first || !nextCursor || loadingMore) return;
    const base = first;
    setLoadingMore(true);
    try {
      const page = await fetchPage(nextCursor);
      if (firstRef.current !== base) return;
      setMore({ base, items: [...(extra?.items ?? []), ...(page.items ?? [])], cursor: page.nextCursor ?? null });
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
    } finally {
      setLoadingMore(false);
    }
  };

  const when = (v: string | null | undefined) => formatInstant(v, zone, { offsets });
  const error = q.error ? asApiError(q.error) : null;
  return (
    <div data-testid="jobs-tab" className="jobs-tab">
      <div className="page-toolbar jobs-head">
        <span className="muted small">
          {rows.length} job{rows.length === 1 ? '' : 's'} loaded · times in {zoneLabel(zone, { offsets })}
        </span>
        <div className="spacer" />
        <Button size="s" icon={<RefreshCw aria-hidden="true" />} onClick={() => queryCache.invalidate(key)} busy={q.loading && !!first}>
          Refresh
        </Button>
      </div>
      {error && <LoadError>{error.message}</LoadError>}
      <div className="pane">
      {q.loading && !first && <SkeletonRows rows={4} />}
      {first && rows.length === 0 && <EmptyState icon={<Hourglass />} title="No job yet." body="A release build queues one; its progress and result show here." />}
      <ul className="job-rows" aria-label="Jobs">
        {rows.map((job) => {
          const jobError = job.error as JobError | null;
          const issues = job.kind === 'release.build' && job.status === 'failed' ? issuesOf(jobError) : null;
          return (
            <li key={job.id}>
              <details className="job-row" data-testid={`job-${job.id}`}>
                <summary>
                  <ChevronRight className="job-chevron" aria-hidden="true" />
                  <code className="job-kind">{job.kind}</code>
                  <Badge tone={JOB_TONE[job.status] ?? 'neutral'}>{job.status}</Badge>
                  <span className="job-progress">
                    <progress max={100} value={Number(job.progress) || 0} aria-hidden="true" />
                    {String(job.progress)}%{job.progressMessage ? ` · ${job.progressMessage}` : ''}
                  </span>
                  <span className="small">{job.createdBy?.label ?? '—'}</span>
                  <span className="small muted" title="created · started · finished">
                    created {when(job.createdAt)} · started {when(job.startedAt)} · finished {when(job.finishedAt)}
                  </span>
                </summary>
                <div className="job-detail">
                  <h4>Input</h4>
                  <pre className="json">{pretty(job.input)}</pre>
                  {job.error ? (
                    <>
                      <h4>Error</h4>
                      {jobError?.message && (
                        <LoadError>
                          {jobError.code ? `${jobError.code}: ` : ''}
                          {jobError.message}
                        </LoadError>
                      )}
                      {issues ? <ValidationReport issues={issues} branch={branch} /> : <pre className="json">{pretty(job.error)}</pre>}
                    </>
                  ) : (
                    <>
                      <h4>Result</h4>
                      <pre className="json">{pretty(job.result)}</pre>
                    </>
                  )}
                </div>
              </details>
            </li>
          );
        })}
      </ul>
      </div>
      {nextCursor && (
        <div className="row jobs-more">
          <Button size="s" data-testid="jobs-load-more" onClick={() => void loadMore()} busy={loadingMore}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}
